import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../design-system/Button';
import { Card } from '../design-system/Card';
import { coachOnboardingAPI } from '../../api/coachApi';
import { OnboardingProgress } from '../../types/coach';
import BasicInfoStep from './onboarding/BasicInfoStep';
import ProfessionalInfoStep from './onboarding/ProfessionalInfoStep';
import BusinessSetupStep from './onboarding/BusinessSetupStep';
import AvailabilityStep from './onboarding/AvailabilityStep';
import PricingStep from './onboarding/PricingStep';
import MarketingStep from './onboarding/MarketingStep';
import ComplianceStep from './onboarding/ComplianceStep';
import ReviewStep from './onboarding/ReviewStep';

interface StepConfig {
  key: string;
  title: string;
  subtitle: string;
  component: React.ComponentType<any>;
  required: boolean;
}

const ONBOARDING_STEPS: StepConfig[] = [
  {
    key: 'basic_info',
    title: 'Basic Information',
    subtitle: 'Tell us about yourself and your coaching style',
    component: BasicInfoStep,
    required: true,
  },
  {
    key: 'professional_info',
    title: 'Professional Information',
    subtitle: 'Share your qualifications and experience',
    component: ProfessionalInfoStep,
    required: true,
  },
  {
    key: 'business_setup',
    title: 'Business Setup',
    subtitle: 'Configure your business settings',
    component: BusinessSetupStep,
    required: true,
  },
  {
    key: 'availability',
    title: 'Availability',
    subtitle: 'Set your working hours and availability',
    component: AvailabilityStep,
    required: true,
  },
  {
    key: 'pricing',
    title: 'Pricing & Services',
    subtitle: 'Define your service packages and pricing',
    component: PricingStep,
    required: true,
  },
  {
    key: 'marketing',
    title: 'Marketing Profile',
    subtitle: 'Create your public-facing profile',
    component: MarketingStep,
    required: false,
  },
  {
    key: 'compliance',
    title: 'Compliance & Insurance',
    subtitle: 'Upload compliance documents (optional)',
    component: ComplianceStep,
    required: false,
  },
  {
    key: 'review',
    title: 'Review & Complete',
    subtitle: 'Review your profile and start accepting clients',
    component: ReviewStep,
    required: true,
  },
];

const CoachOnboarding: React.FC = () => {
  const navigate = useNavigate();
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [progress, setProgress] = useState<OnboardingProgress | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchProgress();
  }, []);

  const fetchProgress = async () => {
    try {
      setLoading(true);
      const data = await coachOnboardingAPI.getProgress();
      setProgress(data);
      
      // Find current step index
      const stepIndex = ONBOARDING_STEPS.findIndex(
        step => step.key === data.current_step
      );
      setCurrentStepIndex(stepIndex >= 0 ? stepIndex : 0);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleStepComplete = async () => {
    // Move to next step
    if (currentStepIndex < ONBOARDING_STEPS.length - 1) {
      setCurrentStepIndex(currentStepIndex + 1);
    }
    
    // Refresh progress
    await fetchProgress();
  };

  const handleSkipStep = () => {
    // Skip to next step (for optional steps)
    if (currentStepIndex < ONBOARDING_STEPS.length - 1) {
      setCurrentStepIndex(currentStepIndex + 1);
    }
  };

  const handleComplete = async () => {
    try {
      await coachOnboardingAPI.completeOnboarding();
      navigate('/dashboard');
    } catch (err: any) {
      setError(err.message);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#4B5FDC] mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading your progress...</p>
        </div>
      </div>
    );
  }

  const currentStep = ONBOARDING_STEPS[currentStepIndex];
  const StepComponent = currentStep.component;
  const progressPercentage = progress?.profile_completion_percentage || 0;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <h1 className="text-2xl font-bold text-gray-900">Coach Onboarding</h1>
            <span className="text-sm text-gray-600">
              {progressPercentage}% Complete
            </span>
          </div>
          
          {/* Progress Bar */}
          <div className="mt-4">
            <div className="bg-gray-200 rounded-full h-2">
              <div
                className="bg-[#4B5FDC] h-2 rounded-full transition-all duration-300"
                style={{ width: `${progressPercentage}%` }}
              />
            </div>
          </div>
          
          {/* Step Indicators */}
          <div className="mt-6 flex justify-between">
            {ONBOARDING_STEPS.map((step, index) => (
              <div
                key={step.key}
                className="flex flex-col items-center flex-1"
              >
                <div
                  className={`
                    w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium
                    ${index === currentStepIndex 
                      ? 'bg-[#4B5FDC] text-white' 
                      : index < currentStepIndex || progress?.completed_steps.includes(step.key)
                        ? 'bg-green-500 text-white' 
                        : 'bg-gray-300 text-gray-600'
                    }
                  `}
                >
                  {index < currentStepIndex || progress?.completed_steps.includes(step.key) 
                    ? '✓' 
                    : index + 1
                  }
                </div>
                <span className="text-xs text-gray-600 mt-1 text-center hidden md:block">
                  {step.title}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-4xl mx-auto px-4 py-8">
        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
            {error}
          </div>
        )}
        
        <Card className="p-8">
          <div className="mb-6">
            <h2 className="text-2xl font-bold text-gray-900">{currentStep.title}</h2>
            <p className="text-gray-600 mt-1">{currentStep.subtitle}</p>
          </div>
          
          <StepComponent
            onComplete={handleStepComplete}
            onSkip={currentStep.required ? undefined : handleSkipStep}
            onFinish={currentStepIndex === ONBOARDING_STEPS.length - 1 ? handleComplete : undefined}
            progress={progress}
          />
        </Card>
        
        {/* Navigation */}
        <div className="mt-8 flex justify-between">
          <Button
            variant="ghost"
            onClick={() => setCurrentStepIndex(Math.max(0, currentStepIndex - 1))}
            disabled={currentStepIndex === 0}
          >
            Previous
          </Button>
          
          <div className="flex gap-4">
            {!currentStep.required && currentStepIndex < ONBOARDING_STEPS.length - 1 && (
              <Button
                variant="secondary"
                onClick={handleSkipStep}
              >
                Skip
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CoachOnboarding;